import { supabase } from '../lib/supabase';

class VideoService {
  // Get all videos with analytics for admin view
  async getAllVideos(sortBy = 'created_at', sortOrder = 'desc') {
    try {
      const { data, error } = await supabase?.from('song_videos')?.select(`
          id,
          level,
          subsection,
          video_path,
          thumbnail_path,
          duration_seconds,
          character_analysis_id,
          created_at,
          talent_profiles(
            id,
            first_name,
            last_name
          ),
          character_song_analyses(
            id,
            roles(
              id,
              character_name,
              musicals(title)
            ),
            songs(
              id,
              title
            )
          ),
          video_analytics(
            play_count,
            total_watch_time_seconds,
            first_five_seconds_played
          )
        `)?.order(sortBy, { ascending: sortOrder === 'asc' });

      if (error) {
        throw error;
      }

      // Process data to aggregate analytics and enhance with character info
      const processedData = data?.map(video => {
        const totalPlays = video?.video_analytics?.reduce((sum, analytics) => sum + (analytics?.play_count || 0), 0);
        const totalWatchTime = video?.video_analytics?.reduce((sum, analytics) => sum + (analytics?.total_watch_time_seconds || 0), 0);
        const firstFiveSecondsPlayed = video?.video_analytics?.filter(analytics => analytics?.first_five_seconds_played)?.length || 0;

        // Create searchable text for character-specific analysis
        const characterInfo = video?.character_song_analyses;
        const songTitle = characterInfo?.songs?.title || 'Unknown Song';
        const characterName = characterInfo?.roles?.character_name || 'Unknown Character';
        const musicalTitle = characterInfo?.roles?.musicals?.title || 'Unknown Musical';
        
        const searchableText = `${songTitle}, ${characterName}, Level ${video?.level}${video?.subsection ? `, Section ${video?.subsection}` : ''}`;

        return {
          ...video,
          analytics: {
            totalPlays,
            totalWatchTime,
            firstFiveSecondsPlayed
          },
          characterSpecificAnalysis: {
            songTitle,
            characterName, 
            musicalTitle,
            searchableText
          }
        };
      }) || [];

      return { data: processedData, error: null };
    } catch (error) {
      return { data: [], error };
    }
  }

  // Search videos by character-specific analysis
  async searchVideosByCharacterAnalysis(searchQuery = '') {
    try {
      const { data, error } = await supabase?.rpc('search_videos_by_character_analysis', {
        search_query: searchQuery
      });

      if (error) {
        throw error;
      }

      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error };
    }
  }

  // Enhanced video upload with metadata extraction
  async uploadVideo(file, videoPath, thumbnailFile = null, metadata = {}) {
    try {
      // Upload video file
      const { data: videoData, error: videoError } = await supabase?.storage?.from('song-videos')?.upload(videoPath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (videoError) {
        throw videoError;
      }

      let thumbnailPath = null;
      if (thumbnailFile) {
        const thumbnailStoragePath = videoPath?.replace(/\.[^/.]+$/, '_thumbnail.jpg');
        const { data: thumbnailData, error: thumbnailError } = await supabase?.storage?.from('video-thumbnails')?.upload(thumbnailStoragePath, thumbnailFile, {
            cacheControl: '86400',
            upsert: true
          });

        if (thumbnailError) {
          console.warn('Thumbnail upload failed:', thumbnailError);
        } else {
          thumbnailPath = thumbnailData?.path;
        }
      }

      return { 
        videoPath: videoData?.path, 
        thumbnailPath,
        metadata: metadata,
        error: null 
      };
    } catch (error) {
      return { videoPath: null, thumbnailPath: null, metadata: {}, error };
    }
  }

  // Enhanced video URL generation with signed URLs for private access
  async getVideoUrl(videoPath) {
    try {
      // Validate videoPath first
      if (!videoPath || typeof videoPath !== 'string') {
        console.warn('Invalid video path provided:', videoPath);
        return null;
      }

      // First try to get public URL
      const { data: publicData } = supabase?.storage?.from('song-videos')?.getPublicUrl(videoPath);
      
      // For private buckets, create signed URL
      const { data: signedData, error } = await supabase?.storage?.from('song-videos')?.createSignedUrl(videoPath, 3600); // 1 hour expiry
      
      if (error) {
        console.warn('Signed URL creation failed, using public URL:', error);
        return publicData?.publicUrl || null;
      }
      
      return signedData?.signedUrl || publicData?.publicUrl || null;
    } catch (error) {
      console.error('Error getting video URL:', error);
      return null;
    }
  }

  // Enhanced thumbnail URL generation
  async getThumbnailUrl(thumbnailPath) {
    try {
      // Validate thumbnailPath first
      if (!thumbnailPath || typeof thumbnailPath !== 'string') return null;
      
      const { data } = supabase?.storage?.from('video-thumbnails')?.getPublicUrl(thumbnailPath);
      return data?.publicUrl || null;
    } catch (error) {
      return null;
    }
  }

  // Enhanced video record creation with metadata
  async createVideoRecord(videoData, userId) {
    try {
      const { data, error } = await supabase?.from('song_videos')?.insert({
          video_path: videoData?.video_path,
          thumbnail_path: videoData?.thumbnail_path,
          level: videoData?.level,
          subsection: videoData?.subsection,
          song_id: videoData?.song_id,
          character_analysis_id: videoData?.character_analysis_id,
          talent_id: videoData?.talent_id,
          duration_seconds: videoData?.duration_seconds || 0,
          file_size_bytes: videoData?.file_size_bytes || 0,
          created_by: userId
        })?.select()?.single();

      if (error) {
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  // Get videos for a song with character analysis context
  async getSongVideos(songId) {
    try {
      const { data, error } = await supabase?.from('song_videos')?.select(`
          id,
          level,
          subsection,
          video_path,
          thumbnail_path,
          duration_seconds,
          file_size_bytes,
          character_analysis_id,
          created_at,
          talent_profiles(
            id,
            first_name,
            last_name
          ),
          character_song_analyses(
            id,
            roles(
              id,
              character_name
            )
          )
        `)?.eq('song_id', songId)?.order('level')?.order('subsection');

      if (error) {
        throw error;
      }

      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error };
    }
  }

  // Get videos for specific character analysis
  async getCharacterAnalysisVideos(characterAnalysisId) {
    try {
      const { data, error } = await supabase?.from('song_videos')?.select(`
          id,
          level,
          subsection,
          video_path,
          thumbnail_path,
          duration_seconds,
          file_size_bytes,
          created_at,
          talent_profiles(
            id,
            first_name,
            last_name
          )
        `)?.eq('character_analysis_id', characterAnalysisId)?.order('level')?.order('subsection');

      if (error) {
        throw error;
      }

      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error };
    }
  }

  // Enhanced video analytics tracking
  async trackVideoPlay(videoId, userId, watchTimeSeconds = 0, playedFirstFiveSeconds = false) {
    try {
      if (!videoId || !userId) return { error: 'Video ID and User ID required' };

      // Check if user already has analytics for this video
      const { data: existingAnalytics, error: fetchError } = await supabase?.from('video_analytics')?.select('id, play_count, total_watch_time_seconds, first_five_seconds_played')?.eq('video_id', videoId)?.eq('user_id', userId)?.single();

      if (fetchError && fetchError?.code !== 'PGRST116') { // Not found error is OK
        throw fetchError;
      }

      if (existingAnalytics) {
        // Update existing analytics
        const { error: updateError } = await supabase?.from('video_analytics')?.update({
            play_count: existingAnalytics?.play_count + (watchTimeSeconds === 0 ? 1 : 0), // Only increment play count on initial play
            total_watch_time_seconds: existingAnalytics?.total_watch_time_seconds + watchTimeSeconds,
            first_five_seconds_played: existingAnalytics?.first_five_seconds_played || playedFirstFiveSeconds,
            last_watched_at: new Date()?.toISOString(),
            updated_at: new Date()?.toISOString()
          })?.eq('id', existingAnalytics?.id);

        if (updateError) {
          throw updateError;
        }
      } else {
        // Create new analytics record
        const { error: insertError } = await supabase?.from('video_analytics')?.insert({
            video_id: videoId,
            user_id: userId,
            play_count: 1,
            total_watch_time_seconds: watchTimeSeconds,
            first_five_seconds_played: playedFirstFiveSeconds,
            last_watched_at: new Date()?.toISOString()
          });

        if (insertError) {
          throw insertError;
        }
      }

      return { error: null };
    } catch (error) {
      console.error('Error tracking video analytics:', error);
      return { error };
    }
  }

  // Enhanced video analytics retrieval
  async getVideoAnalytics(videoId, userId = null) {
    try {
      let query = supabase?.from('video_analytics')?.select('*')?.eq('video_id', videoId);
      
      if (userId) {
        query = query?.eq('user_id', userId);
      }
      
      const { data, error } = await query;
      
      if (error) {
        throw error;
      }

      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error };
    }
  }

  // Get aggregated analytics for a video
  async getVideoStats(videoId) {
    try {
      const { data, error } = await supabase?.from('video_analytics')?.select('play_count, total_watch_time_seconds, first_five_seconds_played')?.eq('video_id', videoId);
      
      if (error) {
        throw error;
      }

      const analytics = data || [];
      const stats = {
        totalPlays: analytics?.reduce((sum, record) => sum + (record?.play_count || 0), 0),
        totalWatchTime: analytics?.reduce((sum, record) => sum + (record?.total_watch_time_seconds || 0), 0),
        uniqueViewers: analytics?.length,
        completionRate: analytics?.filter(record => record?.first_five_seconds_played)?.length
      };

      return { data: stats, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  // Delete video and cleanup storage
  async deleteVideo(videoId) {
    try {
      // Get video details first
      const { data: video, error: fetchError } = await supabase?.from('song_videos')?.select('video_path, thumbnail_path')?.eq('id', videoId)?.single();

      if (fetchError) {
        throw fetchError;
      }

      // Delete video file from storage
      if (video?.video_path) {
        const { error: videoStorageError } = await supabase?.storage?.from('song-videos')?.remove([video?.video_path]);
        if (videoStorageError) {
          console.warn('Error removing video file:', videoStorageError);
        }
      }

      // Delete thumbnail from storage
      if (video?.thumbnail_path) {
        const { error: thumbnailStorageError } = await supabase?.storage?.from('video-thumbnails')?.remove([video?.thumbnail_path]);
        if (thumbnailStorageError) {
          console.warn('Error removing thumbnail file:', thumbnailStorageError);
        }
      }

      // Delete video analytics
      const { error: analyticsError } = await supabase?.from('video_analytics')?.delete()?.eq('video_id', videoId);
      if (analyticsError) {
        console.warn('Error removing video analytics:', analyticsError);
      }

      // Delete video record from database
      const { error: deleteError } = await supabase?.from('song_videos')?.delete()?.eq('id', videoId);

      if (deleteError) {
        throw deleteError;
      }

      return { error: null };
    } catch (error) {
      return { error };
    }
  }
}

// Export as default service instance
const videoServiceInstance = new VideoService();
export default videoServiceInstance;

// Export individual methods for backward compatibility
export const uploadVideo = (file, videoPath, thumbnailFile, metadata) => 
  videoServiceInstance?.uploadVideo(file, videoPath, thumbnailFile, metadata);

export const createVideoRecord = (videoData, userId) => 
  videoServiceInstance?.createVideoRecord(videoData, userId);

export const deleteVideo = (videoId) => 
  videoServiceInstance?.deleteVideo(videoId);

export const getVideoUrl = (videoPath) => 
  videoServiceInstance?.getVideoUrl(videoPath);

export const getThumbnailUrl = (thumbnailPath) => 
  videoServiceInstance?.getThumbnailUrl(thumbnailPath);

export const trackVideoPlay = (videoId, userId, watchTimeSeconds, playedFirstFiveSeconds) => 
  videoServiceInstance?.trackVideoPlay(videoId, userId, watchTimeSeconds, playedFirstFiveSeconds);

export const getVideoAnalytics = (videoId, userId) => 
  videoServiceInstance?.getVideoAnalytics(videoId, userId);

export const getVideoStats = (videoId) => 
  videoServiceInstance?.getVideoStats(videoId);

export const getAllVideosWithAnalytics = (sortBy, sortOrder) => 
  videoServiceInstance?.getAllVideos(sortBy, sortOrder);

export const getSongVideos = (songId) => 
  videoServiceInstance?.getSongVideos(songId);

export const searchVideosByCharacterAnalysis = (searchQuery) =>
  videoServiceInstance?.searchVideosByCharacterAnalysis(searchQuery);